/**
 * Schedule Serializer
 *
 * Serializes structured schedule data back to EnergyPlus IDF format.
 */

import type { IDFField, IDFObject } from '../parser';
import type { ParsedSchedule, ScheduleTypeLimits } from './types';

/**
 * Generate a unique ID for IDF objects
 */
function generateId(): string {
  return `obj_${String(Date.now())}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Format a date as "M/D"
 */
function formatDate(month: number, day: number): string {
  return `${String(month)}/${String(day)}`;
}

/**
 * Format time as "HH:MM"
 */
function formatTime(hour: number, minute: number): string {
  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
}

/**
 * Serialize a Schedule:Compact to IDF format
 */
export function serializeScheduleCompact(schedule: ParsedSchedule): string {
  const lines: string[] = ['Schedule:Compact,'];
  lines.push(`  ${schedule.name},                    !- Name`);
  lines.push(`  ${schedule.typeLimits || ''},        !- Schedule Type Limits Name`);

  for (let pIdx = 0; pIdx < schedule.periods.length; pIdx++) {
    const period = schedule.periods[pIdx];
    if (!period) continue;

    lines.push(`  Through: ${formatDate(period.endDate.month, period.endDate.day)},  !- Field`);

    for (let dsIdx = 0; dsIdx < period.daySchedules.length; dsIdx++) {
      const daySchedule = period.daySchedules[dsIdx];
      if (!daySchedule) continue;

      const dayTypesStr = daySchedule.dayTypes.join(' ');
      lines.push(`  For: ${dayTypesStr},               !- Field`);

      for (let vIdx = 0; vIdx < daySchedule.values.length; vIdx++) {
        const tv = daySchedule.values[vIdx];
        if (!tv) continue;

        const isLast =
          vIdx === daySchedule.values.length - 1 &&
          dsIdx === period.daySchedules.length - 1 &&
          pIdx === schedule.periods.length - 1;

        const terminator = isLast ? ';' : ',';
        const timeStr = formatTime(tv.hour, tv.minute);
        lines.push(`  Until: ${timeStr}, ${String(tv.value)}${terminator}     !- Field`);
      }
    }
  }

  return lines.join('\n');
}

/**
 * Serialize a Schedule:Constant to IDF format
 */
export function serializeScheduleConstant(schedule: ParsedSchedule): string {
  const value = schedule.constantValue ?? 1;
  const lines: string[] = ['Schedule:Constant,'];
  lines.push(`  ${schedule.name},                    !- Name`);
  lines.push(`  ${schedule.typeLimits || ''},        !- Schedule Type Limits Name`);
  lines.push(`  ${String(value)};                    !- Hourly Value`);

  return lines.join('\n');
}

/**
 * Serialize a parsed schedule to IDF text format
 */
export function serializeSchedule(schedule: ParsedSchedule): string {
  switch (schedule.scheduleType) {
    case 'Schedule:Compact':
      return serializeScheduleCompact(schedule);
    case 'Schedule:Constant':
      return serializeScheduleConstant(schedule);
    default:
      // For other types, default to Schedule:Compact
      return serializeScheduleCompact(schedule);
  }
}

/**
 * Convert a ParsedSchedule to an IDFObject
 */
export function scheduleToIDFObject(schedule: ParsedSchedule): IDFObject {
  if (schedule.scheduleType === 'Schedule:Constant') {
    return scheduleConstantToIDFObject(schedule);
  }
  return scheduleCompactToIDFObject(schedule);
}

/**
 * Convert a Schedule:Compact to an IDFObject
 */
export function scheduleCompactToIDFObject(schedule: ParsedSchedule): IDFObject {
  const fields: IDFField[] = [
    { value: schedule.name, inlineComment: 'Name' },
    { value: schedule.typeLimits || '', inlineComment: 'Schedule Type Limits Name' },
  ];

  for (const period of schedule.periods) {
    fields.push({
      value: `Through: ${formatDate(period.endDate.month, period.endDate.day)}`,
      inlineComment: 'Field',
    });

    for (const daySchedule of period.daySchedules) {
      const dayTypesStr = daySchedule.dayTypes.join(' ');
      fields.push({
        value: `For: ${dayTypesStr}`,
        inlineComment: 'Field',
      });

      for (const tv of daySchedule.values) {
        fields.push({
          value: `Until: ${formatTime(tv.hour, tv.minute)}`,
          inlineComment: 'Field',
        });
        fields.push({
          value: String(tv.value),
          inlineComment: 'Field',
        });
      }
    }
  }

  return {
    id: schedule.idfObjectId || generateId(),
    className: 'Schedule:Compact',
    fields,
    leadingComments: [],
  };
}

/**
 * Convert a Schedule:Constant to an IDFObject
 */
export function scheduleConstantToIDFObject(schedule: ParsedSchedule): IDFObject {
  const value = schedule.constantValue ?? 1;

  return {
    id: schedule.idfObjectId || generateId(),
    className: 'Schedule:Constant',
    fields: [
      { value: schedule.name, inlineComment: 'Name' },
      { value: schedule.typeLimits || '', inlineComment: 'Schedule Type Limits Name' },
      { value: String(value), inlineComment: 'Hourly Value' },
    ],
    leadingComments: [],
  };
}

/**
 * Serialize ScheduleTypeLimits to IDF text format
 */
export function serializeScheduleTypeLimits(limits: ScheduleTypeLimits): string {
  const lines: string[] = ['ScheduleTypeLimits,'];
  lines.push(`  ${limits.name},                      !- Name`);
  lines.push(`  ${limits.lowerLimit !== undefined ? String(limits.lowerLimit) : ''},  !- Lower Limit Value`);
  lines.push(`  ${limits.upperLimit !== undefined ? String(limits.upperLimit) : ''},  !- Upper Limit Value`);
  lines.push(`  ${limits.numericType || ''},         !- Numeric Type`);
  lines.push(`  ${limits.unitType || ''};            !- Unit Type`);

  return lines.join('\n');
}

/**
 * Convert ScheduleTypeLimits to an IDFObject
 */
export function scheduleTypeLimitsToIDFObject(limits: ScheduleTypeLimits): IDFObject {
  return {
    id: generateId(),
    className: 'ScheduleTypeLimits',
    fields: [
      { value: limits.name, inlineComment: 'Name' },
      { value: limits.lowerLimit !== undefined ? String(limits.lowerLimit) : '', inlineComment: 'Lower Limit Value' },
      { value: limits.upperLimit !== undefined ? String(limits.upperLimit) : '', inlineComment: 'Upper Limit Value' },
      { value: limits.numericType || '', inlineComment: 'Numeric Type' },
      { value: limits.unitType || '', inlineComment: 'Unit Type' },
    ],
    leadingComments: [],
  };
}

/**
 * Create a default Schedule:Compact template
 */
export function createDefaultScheduleCompact(name: string, typeLimits: string | null = null): ParsedSchedule {
  const id = `sch_${String(Date.now())}_${Math.random().toString(36).substring(2, 9)}`;
  const periodId = `sch_${String(Date.now())}_${Math.random().toString(36).substring(2, 9)}`;

  return {
    id,
    name,
    typeLimits,
    scheduleType: 'Schedule:Compact',
    periods: [
      {
        id: periodId,
        startDate: { month: 1, day: 1 },
        endDate: { month: 12, day: 31 },
        daySchedules: [
          {
            dayTypes: ['AllDays'],
            values: [{ hour: 24, minute: 0, value: 1 }],
          },
        ],
      },
    ],
  };
}

/**
 * Create a default Schedule:Constant
 */
export function createDefaultScheduleConstant(
  name: string,
  value: number = 1,
  typeLimits: string | null = null
): ParsedSchedule {
  const id = `sch_${String(Date.now())}_${Math.random().toString(36).substring(2, 9)}`;
  const periodId = `sch_${String(Date.now())}_${Math.random().toString(36).substring(2, 9)}`;

  return {
    id,
    name,
    typeLimits,
    scheduleType: 'Schedule:Constant',
    periods: [
      {
        id: periodId,
        startDate: { month: 1, day: 1 },
        endDate: { month: 12, day: 31 },
        daySchedules: [
          {
            dayTypes: ['AllDays'],
            values: [{ hour: 24, minute: 0, value }],
          },
        ],
      },
    ],
    constantValue: value,
  };
}
